export const GREAT_KINGDOM_BOARD_SIZE = 9;
export const GREAT_KINGDOM_CENTER = { row: 4, col: 4 } as const;
export const GREAT_KINGDOM_PLAYERS = ["p1", "p2"] as const;

export type GreatKingdomPlayerId = (typeof GREAT_KINGDOM_PLAYERS)[number];
export type GreatKingdomCell = "empty" | "neutral" | GreatKingdomPlayerId;
export type GreatKingdomBoard = readonly (readonly GreatKingdomCell[])[];
export type GreatKingdomSetupMode = "basic" | "advanced";
export type GreatKingdomPhase = "neutral-setup" | "playing" | "game-over";
export type GreatKingdomEndReason = "siege" | "double-pass";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface GreatKingdomPosition {
  readonly row: number;
  readonly col: number;
}

export interface GreatKingdomPlayer {
  readonly playerId: GreatKingdomPlayerId;
  readonly label: string;
  readonly color: "blue" | "orange";
  readonly scoreEstimate: number;
}

export interface GreatKingdomTerritorySummary {
  readonly p1: number;
  readonly p2: number;
}

export interface GreatKingdomTerritoryRegion {
  readonly owner: GreatKingdomPlayerId | null;
  readonly cells: readonly GreatKingdomPosition[];
  readonly touchesAllEdges: boolean;
}

export interface GreatKingdomCapturedGroup {
  readonly owner: GreatKingdomPlayerId;
  readonly cells: readonly GreatKingdomPosition[];
}

export interface GreatKingdomAttackWarning {
  readonly owner: GreatKingdomPlayerId;
  readonly group: readonly GreatKingdomPosition[];
  readonly liberty: GreatKingdomPosition;
}

export interface GreatKingdomState {
  readonly gameId: string;
  readonly hostPlayerId: GreatKingdomPlayerId;
  readonly players: readonly GreatKingdomPlayer[];
  readonly setupMode: GreatKingdomSetupMode;
  readonly phase: GreatKingdomPhase;
  readonly board: GreatKingdomBoard;
  readonly currentPlayerId: GreatKingdomPlayerId;
  readonly moveNumber: number;
  readonly passStreak: number;
  readonly territory: GreatKingdomTerritorySummary;
  readonly capturedGroups: readonly GreatKingdomCapturedGroup[];
  readonly winnerId: GreatKingdomPlayerId | null;
  readonly endReason: GreatKingdomEndReason | null;
  readonly lastAction: GreatKingdomAction | null;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly hostPlayerId: GreatKingdomPlayerId;
  readonly firstPlayerId?: GreatKingdomPlayerId;
  readonly setupMode?: GreatKingdomSetupMode;
}

export interface PlaceNeutralAction {
  readonly type: "PlaceNeutral";
  readonly playerId: GreatKingdomPlayerId;
  readonly row: number;
  readonly col: number;
}

export interface PlaceCastleAction {
  readonly type: "PlaceCastle";
  readonly playerId: GreatKingdomPlayerId;
  readonly row: number;
  readonly col: number;
}

export interface PassAction {
  readonly type: "Pass";
  readonly playerId: GreatKingdomPlayerId;
}

export type GreatKingdomAction =
  | PlaceNeutralAction
  | PlaceCastleAction
  | PassAction;

export type GreatKingdomViewerState = GreatKingdomState;
